export type DiffKind = 'same' | 'added' | 'removed';

export interface DiffLine {
  kind: DiffKind;
  text: string;
}

export type DiffRow = DiffLine | { kind: 'skip'; count: number };

function splitLines(text: string): string[] {
  return text === '' ? [] : text.replace(/\r\n/g, '\n').split('\n');
}

/** Line diff via LCS. `removed` lines exist only in `oldText`, `added` only in `newText`. */
export function lineDiff(oldText: string, newText: string): DiffLine[] {
  const a = splitLines(oldText);
  const b = splitLines(newText);
  const n = a.length;
  const m = b.length;
  const w = m + 1;
  // lcs[i * w + j] = LCS length of a[i..] and b[j..]
  const lcs = new Int32Array((n + 1) * w);
  const at = (i: number, j: number): number => lcs[i * w + j] ?? 0;
  for (let i = n - 1; i >= 0; i--) {
    for (let j = m - 1; j >= 0; j--) {
      lcs[i * w + j] = a[i] === b[j] ? at(i + 1, j + 1) + 1 : Math.max(at(i + 1, j), at(i, j + 1));
    }
  }

  const out: DiffLine[] = [];
  let i = 0;
  let j = 0;
  while (i < n || j < m) {
    if (i < n && j < m && a[i] === b[j]) {
      out.push({ kind: 'same', text: a[i] ?? '' });
      i++;
      j++;
    } else if (j >= m || (i < n && at(i + 1, j) >= at(i, j + 1))) {
      out.push({ kind: 'removed', text: a[i++] ?? '' });
    } else {
      out.push({ kind: 'added', text: b[j++] ?? '' });
    }
  }
  return out;
}

/** Collapses long runs of unchanged lines, keeping `context` lines next to each change. */
export function collapseUnchanged(lines: DiffLine[], context = 3): DiffRow[] {
  const out: DiffRow[] = [];
  let k = 0;
  while (k < lines.length) {
    const line = lines[k];
    if (line && line.kind !== 'same') {
      out.push(line);
      k++;
      continue;
    }
    let end = k;
    while (end < lines.length && lines[end]?.kind === 'same') end++;
    const keepHead = k === 0 ? 0 : context;
    const keepTail = end === lines.length ? 0 : context;
    const run = end - k;
    if (run > keepHead + keepTail + 1) {
      out.push(...lines.slice(k, k + keepHead));
      out.push({ kind: 'skip', count: run - keepHead - keepTail });
      out.push(...lines.slice(end - keepTail, end));
    } else {
      out.push(...lines.slice(k, end));
    }
    k = end;
  }
  return out;
}
